import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import React from 'react';

import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { CountdownDate } from '@flipster/react/design-utils/countdown-date';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { Styles, css } from '@flipster/styled-system/css';
import { flex, stack } from '@flipster/styled-system/patterns';
import { DateFormatter } from '@flipster/utils/datetime';

import StakingStatusBadge from '../LaunchpoolStatusBadge';
import { sparkLaunchpoolItemBunja } from './bunja';
import { Hr } from './misc';

const SparkLaunchpoolInfo = () => {
  const t = useTranslations();
  const {
    launchpoolItemAtom,
    isClosedAtom,
    isUpcomingAtom,
    isOngoingAtom,
    isSameYearAtom,
    rewardCurrencyPrecision,
    stakingCurrencyPrecision,
  } = useBunja(sparkLaunchpoolItemBunja);
  const launchpool = useAtomValue(launchpoolItemAtom);
  const { status, totalReward, rewardCurrency, startAt, endAt, estimatedDailyReward, totalStaked, stakedAmount } =
    launchpool;
  const isClosed = useAtomValue(isClosedAtom);
  const isUpcoming = useAtomValue(isUpcomingAtom);
  const isOngoing = useAtomValue(isOngoingAtom);
  const isSameYear = useAtomValue(isSameYearAtom);
  return (
    <div data-closed={isClosed} className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
      <dl className={stack({ gap: 8 })}>
        <Row
          label={t('Autogen_common.r251126.s_Total_rewards')}
          value={
            <Formatted.Number
              value={totalReward}
              suffix={<span className={css({ color: 'content.default.level-2' })}>{rewardCurrency}</span>}
              precision={rewardCurrencyPrecision}
              zeroString='-'
            />
          }
        />
        <Row
          label={t('Autogen_common.r251126.s_Event_period')}
          value={
            <>
              <Datetime value={startAt} format={DateFormatter.ShortYearDateTime} isUTC />
              <br />
              {'- '}
              <Datetime
                value={endAt}
                format={isSameYear ? DateFormatter.MonthDateTime : DateFormatter.ShortYearDateTime}
                isUTC
                withUTCText
              />
            </>
          }
        />
        <Row
          label={t('Autogen_common.r251126.s_Status')}
          value={
            <>
              <StakingStatusBadge status={status} />
              {(isUpcoming || isOngoing) && (
                <CountdownDate
                  targetDate={isUpcoming ? startAt : endAt}
                  format='${dd}D ${hh}H ${mm}M ${ss}S left'
                  css={{ color: 'content.default.level-2', mt: 4 }}
                />
              )}
            </>
          }
        />
        <Row
          label={
            <>
              Est. max potential daily reward
              <button
                className={css({ ml: '4', display: 'inline-block', verticalAlign: 'text-bottom' })}
                onClick={alertEstimatedDailyReward}
              >
                <InformationCircleIcon
                  size={16}
                  className={css({ display: 'block', color: 'content.default.level-1' })}
                />
              </button>
            </>
          }
          value={
            <Formatted.Number
              css={{
                textStyle: 'Title/XS',
                color: isClosed ? 'content.default.level-2' : 'content.state.positive',
              }}
              value={estimatedDailyReward}
              suffix={rewardCurrency}
              precision={rewardCurrencyPrecision}
              zeroString='-'
            />
          }
        />
        <Hr />
        <Row
          label={t('Autogen_common.r251126.s_Total_Sparks_invested')}
          value={
            <Formatted.Number
              css={{ textStyle: 'Text/M/Strong' }}
              value={totalStaked}
              suffix={<span className={css({ color: 'content.default.level-2' })}>Sparks</span>}
              precision={stakingCurrencyPrecision}
              zeroString='-'
            />
          }
        />
        <Row
          css={{ mb: 8 }}
          label={t('Autogen_common.r251126.s_You_invested')}
          value={
            <Formatted.Number
              css={{ textStyle: 'Text/M/Strong' }}
              value={stakedAmount}
              suffix={<span className={css({ color: 'content.default.level-2' })}>Sparks</span>}
              precision={stakingCurrencyPrecision}
              zeroString='-'
            />
          }
        />
      </dl>
    </div>
  );
};

export default SparkLaunchpoolInfo;

function alertEstimatedDailyReward() {
  dialog.open({
    title: {
      label: 'Est. max potential daily reward',
    },
    content: (
      <div className={css({ textStyle: 'Text/M/Regular' })}>
        This shows the estimated maximum potential daily reward when you participate with the maximum cap size.
        <br />
        <br />
        Larger amount of total Sparks invested in the Launchpool may reduce this estimate.
        <br />
        <br />
        Calculations use the latest historical data from 00:00 UTC, so actual rewards may vary.
      </div>
    ),
    buttons: {
      primary: <Dialog.Button hierarchy='primary' label='Dismiss' />,
    },
  });
}

interface RowProps {
  label: React.ReactNode;
  value: React.ReactNode;
  css?: Styles;
}
const Row = ({ label, value, css: cssProps }: RowProps) => {
  return (
    <div
      className={css(
        flex.raw({
          justifyContent: 'space-between',
          color: 'content.default.level-5',
          '[data-closed=true] &': { color: 'content.default.level-2' },
        }),
        cssProps,
      )}
    >
      <dt
        className={css({
          color: 'content.default.level-3',
          '[data-closed=true] &': { color: 'content.default.level-2' },
        })}
      >
        {label}
      </dt>
      <dd className={css({ textAlign: 'right' })}>{value}</dd>
    </div>
  );
};
